import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { api } from '../../utils/apiClient';
import './VenuesPanel.css';

interface Venue {
  id: string;
  name: string;
  slug: string;
  adBannerText?: string | null;
  adBannerImageUrl?: string | null;
  adBannerLink?: string | null;
  _count?: { restaurants: number };
}

interface Restaurant {
  id: string;
  name: string;
  slug: string;
  venueId?: string | null;
  venue?: Venue | null;
}

function VenuesPanel() {
  const { t } = useTranslation();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [updatingRestaurantId, setUpdatingRestaurantId] = useState<string | null>(null);

  const fetchVenues = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Venue[]>('/api/venues');
      setVenues(data || []);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg || 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const { data } = await api.get<Restaurant[]>('/api/restaurants');
      setRestaurants(data || []);
    } catch {
      setRestaurants([]);
    }
  };

  useEffect(() => {
    fetchVenues();
    fetchRestaurants();
  }, []);

  const updateVenueAdBanner = async (venueId: string, field: string, value: string | null) => {
    setError('');
    try {
      await api.patch(`/api/venues/${venueId}`, { [field]: value });
      fetchVenues();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg || 'Failed to update venue');
    }
  };

  const updateRestaurantVenue = async (restaurantId: string, venueId: string | null) => {
    setUpdatingRestaurantId(restaurantId);
    setError('');
    try {
      await api.patch(`/api/restaurants/${restaurantId}`, {
        venueId: venueId || null,
      });
      fetchRestaurants();
      fetchVenues();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg || 'Failed to update restaurant');
    } finally {
      setUpdatingRestaurantId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/venues', {
        name: name.trim(),
        slug: slug.trim() || name.toLowerCase().replace(/\s+/g, '-'),
      });
      setName('');
      setSlug('');
      setShowForm(false);
      fetchVenues();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg || 'Failed to create venue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="venues-panel">
      <div className="panel-header">
        <h2>Venues (Food Courts)</h2>
        <div className="panel-actions">
          <motion.button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm(!showForm)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            {showForm ? t('common.cancel') : '+ Add Venue'}
          </motion.button>
          <motion.button
            type="button"
            className="btn-primary"
            onClick={fetchVenues}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            {t('common.refresh')}
          </motion.button>
        </div>
      </div>

      <p className="venues-desc">
        Venues group restaurants for cross-upsell. When a customer orders at Restaurant A in a venue,
        they can see upsells from Restaurant B in the same venue.
      </p>

      {error && <div className="panel-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="venue-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Venue name (e.g. Mall Food Court)"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slug) setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
              }}
              required
            />
            <input
              type="text"
              placeholder="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? '...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="panel-loading">{t('common.loading')}</div>
      ) : (
        <>
          <div className="venues-ad-section">
            <h3>Ad banner (venue-level)</h3>
            <p className="venues-desc">
              Shown to all customers who scan QR from any restaurant in a venue. Configure per venue below.
            </p>
            {venues.length === 0 ? (
              <div className="venue-ad-empty">
                Create a venue above first. Then you can set banner text, image URL, and link for each venue.
              </div>
            ) : (
              venues.map((v) => (
                <div key={v.id} className="venue-ad-card">
                  <div className="venue-ad-card-header">{v.name}</div>
                  <input
                    type="text"
                    placeholder="Banner text"
                    defaultValue={v.adBannerText ?? ''}
                    onBlur={(e) => updateVenueAdBanner(v.id, 'adBannerText', e.target.value.trim() || null)}
                    className="venue-ad-input"
                  />
                  <input
                    type="url"
                    placeholder="Image URL"
                    defaultValue={v.adBannerImageUrl ?? ''}
                    onBlur={(e) => updateVenueAdBanner(v.id, 'adBannerImageUrl', e.target.value.trim() || null)}
                    className="venue-ad-input"
                  />
                  <input
                    type="url"
                    placeholder="Link URL"
                    defaultValue={v.adBannerLink ?? ''}
                    onBlur={(e) => updateVenueAdBanner(v.id, 'adBannerLink', e.target.value.trim() || null)}
                    className="venue-ad-input"
                  />
                </div>
              ))
            )}
          </div>

          {venues.length === 0 ? (
            <div className="panel-empty">
              No venues yet. Create one above to enable cross-upsell between restaurants in the same food court.
            </div>
          ) : (
            <div className="venues-list">
              <h3>Venues</h3>
              {venues.map((v) => (
                <div key={v.id} className="venue-card">
                  <div className="venue-info">
                    <strong>{v.name}</strong>
                    <code>{v.slug}</code>
                    {v._count != null && (
                      <span className="venue-count">{v._count.restaurants} restaurant(s)</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="venues-restaurants-section">
            <h3>Assign restaurants to venues</h3>
            <p className="venues-desc">Change venue for existing restaurants:</p>
            {restaurants.length === 0 ? (
              <div className="panel-empty">No restaurants yet.</div>
            ) : (
              <div className="restaurants-venue-list">
                {restaurants.map((r) => (
                  <div key={r.id} className="restaurant-venue-row">
                    <span className="restaurant-name">{r.name}</span>
                    <select
                      value={r.venueId || ''}
                      onChange={(e) => updateRestaurantVenue(r.id, e.target.value || null)}
                      disabled={updatingRestaurantId === r.id}
                      className="venue-select"
                    >
                      <option value="">— No venue —</option>
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                    {updatingRestaurantId === r.id && <span className="saving">...</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default VenuesPanel;
